import { useEffect, useMemo, useState } from "react";
import { useVideoPlayerRefs } from "../VideoPlayerRefs";
import { useHlsSource } from "../../hooks/useHlsSource";
import { useMp4Source } from "../../hooks/useMp4Source";
import { VideoSourceContext } from ".";
import { useDelayedState } from "../../hooks/useDelayedState";

export interface VideoSourceProps {
  children?: React.ReactNode;
  src?: string;
  startTime?: number;
  transitionDelay?: number;
}

export const VideoSourceProvider = ({
  children,
  src,
  startTime,
  transitionDelay,
}: VideoSourceProps): JSX.Element => {
  const { video } = useVideoPlayerRefs();
  const [duration, setDuration] = useState(video.current?.duration ?? 0);
  const state = useMemo(() => ({ src, startTime }), [src, startTime]);
  const delayedProps = useDelayedState(state, transitionDelay);

  useMp4Source(delayedProps);
  const hlsValue = useHlsSource(delayedProps);

  useEffect(() => {
    if (video.current !== null) {
      const element = video.current;
      const handler = () => {
        setDuration(video.current!.duration);
      };

      element.addEventListener("durationchange", handler);

      return () => {
        element.removeEventListener("durationchange", handler);
      };
    }
  }, [video]);

  const value = useMemo(
    () => ({
      ...(hlsValue ?? {}),
      duration,
    }),

    [duration, hlsValue]
  );

  return (
    <VideoSourceContext.Provider value={value}>
      {children}
    </VideoSourceContext.Provider>
  );
};
